// Whether a vehicle may be put against a block. Dependency-free so Vite and Deno can both import
// it: the UI shortlists candidates with it, the edge function re-runs it as authorisation.
import type { Enums } from "../database.types.ts"
import { USABLE_OPERATIONAL_STATUSES } from "./vehicle.ts"

export type AssignmentReason = "operational_status" | "depot" | "vehicle_group" | "buffer"

// Reason only - each caller words it themselves.
export type AssignmentCheck = { ok: true } | { ok: false; reason: AssignmentReason }

export interface AssignmentVehicle {
  registration_display: string
  operational_status: string | null
  depot_id: string | null
  vehicle_group: string | null
}

export interface AssignmentShift {
  start_time: string
  end_time: string
  depot_id: string | null
  type: Enums<"person_type">
}

export interface AssignmentConflict {
  start_time: string
  end_time: string
}

// Block types each Zerity group may cover. Keys match vehicles.vehicle_group exactly, which the
// sync writes verbatim from Zerity - an unlisted or differently-cased group fails closed.
export const VEHICLE_GROUP_SHIFT_TYPES: Record<string, readonly Enums<"person_type">[]> = {
  "Flex RC1": ["HivedVan", "HivedFlex", "Trial", "AssociateDriver"],
  "Flex RC2": ["HivedVan", "HivedFlex", "Trial"],
  Trials: ["HivedVan", "HivedFlex", "Trial", "AssociateDriver"],
  Collections: ["HivedHgv"],
  HPD: ["TakeHomeVan"],
  DSP: ["Dsp"],
}

// Minimum gap either side of a block before the same van can be on another one.
export const VEHICLE_BUFFER_MS = 2 * 60 * 60 * 1000

// The window another block on the same van must fall outside of. Callers query conflicts with it
// so their candidate list matches what checkVehicleAssignment refuses.
export function bufferWindow(shift: Pick<AssignmentShift, "start_time" | "end_time">) {
  return {
    from: new Date(new Date(shift.start_time).getTime() - VEHICLE_BUFFER_MS).toISOString(),
    to: new Date(new Date(shift.end_time).getTime() + VEHICLE_BUFFER_MS).toISOString(),
  }
}

// First failing criterion. `otherShiftsForVehicle` must already exclude this block and any
// Cancelled one - every other status counts, matching the RLS helper that lets a driver open a van.
export function checkVehicleAssignment(
  vehicle: AssignmentVehicle,
  shift: AssignmentShift,
  otherShiftsForVehicle: readonly AssignmentConflict[],
): AssignmentCheck {
  if (!USABLE_OPERATIONAL_STATUSES.some((status) => status === vehicle.operational_status)) {
    return { ok: false, reason: "operational_status" }
  }

  if (!vehicle.depot_id) {
    return { ok: false, reason: "depot" }
  }

  if (vehicle.depot_id !== shift.depot_id) {
    return { ok: false, reason: "depot" }
  }

  const groupKey = vehicle.vehicle_group ?? ""
  const allowedTypes = VEHICLE_GROUP_SHIFT_TYPES[groupKey] ?? []
  if (!allowedTypes.includes(shift.type)) {
    return { ok: false, reason: "vehicle_group" }
  }

  // Half-open, so a block ending exactly 2 hours before this one starts is allowed.
  const window = bufferWindow(shift)
  const tooClose = (other: AssignmentConflict) =>
    new Date(window.from) < new Date(other.end_time) &&
    new Date(window.to) > new Date(other.start_time)

  if (otherShiftsForVehicle.some(tooClose)) {
    return { ok: false, reason: "buffer" }
  }

  return { ok: true }
}
